import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { showToast } from '../../../../../../helpers/showToast';

// Helper function to get a readable field name from the field path
const getFieldLabel = (fieldPath, t) => {
  const fieldLabels = {
    // Seller fields
    'seller.vatNumber': t('createInvoice.seller-vat-number'),
    'seller.name': t('createInvoice.seller-name'),
    'seller.streetName': t('createInvoice.street-name'),
    'seller.block': t('createInvoice.block'),
    'seller.buildingName': t('createInvoice.building-name'),
    'seller.flatNumber': t('createInvoice.flat-number'),
    'seller.city': t('createInvoice.city'),
    'seller.country': t('createInvoice.country'),

    // Buyer fields
    'buyer.vatNumber': t('createInvoice.buyer-vat-number'),
    'buyer.name': t('createInvoice.buyer-name'),
    'buyer.streetName': t('createInvoice.street-name'),
    'buyer.block': t('createInvoice.block'),
    'buyer.buildingName': t('createInvoice.building-name'),
    'buyer.flatNumber': t('createInvoice.flat-number'),
    'buyer.city': t('createInvoice.city'),
    'buyer.country': t('createInvoice.country'),

    // Third party fields
    'thirdParty.vatNumber': t('createInvoice.third-party-vat-number'),
    'thirdParty.name': t('createInvoice.third-party-name'),

    // Other fields
    items: t('createInvoice.item-information'),
    paymentTerms: t('createInvoice.payment-terms'),
    invoicingPeriodStartDate: t('createInvoice.invoicing-period-start-date'),
    invoicingPeriodEndDate: t('createInvoice.invoicing-period-end-date'),
    precedingInvoiceNumber: t('createInvoice.preceding-invoice-number'),
    precedingInvoiceIssueDate: t('createInvoice.preceding-invoice-issue-date'),
    noteIssuingReason: t('createInvoice.noteIssuingReason'),
    exchangeRate: t('createInvoice.exchange-rate'),
  };

  return (
    fieldLabels[fieldPath] ||
    fieldPath
      .replace(/\./g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
  );
};

// Helper function to flatten nested errors
const flattenErrors = (errors, prefix = '') => {
  let flatErrors = [];

  Object.keys(errors).forEach((key) => {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const error = errors[key];

    if (typeof error === 'string') {
      flatErrors.push({ field: fullPath, message: error });
    } else if (
      typeof error === 'object' &&
      error !== null &&
      !Array.isArray(error)
    ) {
      flatErrors = flatErrors.concat(flattenErrors(error, fullPath));
    } else if (Array.isArray(error)) {
      // Handle array errors (like items validation)
      const arrayErrors = error.filter((e) => e && typeof e === 'object');
      if (arrayErrors.length > 0) {
        flatErrors.push({
          field: fullPath,
          message: 'Some items have validation errors',
        });
      } else if (typeof error[0] === 'string') {
        flatErrors.push({ field: fullPath, message: error[0] });
      }
    }
  });

  return flatErrors;
};

const FormErrorToast = () => {
  const { errors, submitCount } = useFormikContext();
  const { t } = useTranslation();

  useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      const flatErrors = flattenErrors(errors);
      const maxErrorsToShow = 3;

      if (flatErrors.length > 0) {
        const errorMessages = flatErrors
          .slice(0, maxErrorsToShow)
          .map((error) => {
            const fieldLabel = getFieldLabel(error.field, t);
            return `• ${fieldLabel}: ${error.message}`;
          });

        const additionalErrors = flatErrors.length - maxErrorsToShow;
        if (additionalErrors > 0) {
          errorMessages.push(
            `• ...and ${additionalErrors} more error${
              additionalErrors > 1 ? 's' : ''
            }`,
          );
        }

        showToast({
          data: {
            variant: 'error',
            title: t('create_invoice_validation.fill_all_fields'),
            text: errorMessages.join('\n'),
          },
          toastOptions: {
            autoClose: 5000, // Give more time to read multiple errors
          },
        });
      }
    }
  }, [submitCount, t]);

  return null;
};

export default React.memo(FormErrorToast);
